import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    ViewContainerRef,
    ViewEncapsulation
} from '@angular/core';
import {Overlay, OverlayRef} from '@angular/cdk/overlay';
import {TemplatePortal} from '@angular/cdk/portal';
import {MatButton} from '@angular/material/button';
import {finalize, Subject, takeUntil} from 'rxjs';
import {Notification} from 'app/layout/common/notifications/notifications.types';
import {NotificationsService} from 'app/layout/common/notifications/notifications.service';
import {
    AbstractDefaultComponent
} from '../../../shared/abstracts/abstract-default-component/abstract-default-component';
import {LocalStorageService} from '../../../shared/services/local-storage.service';
import {WebSocketService} from '../../../shared/services/web-socket.service';
import {FuseConfirmationService} from '../../../../@fuse/services/confirmation';
import {SnackbarTypes} from '../../../../@fuse/services/confirmation/snackbar/snackbar.component';
import {getTransalate} from '../../../shared/utils/utils';
import {TranslocoService} from '@jsverse/transloco';
import {Router} from '@angular/router';
import {PathEnum} from '../../../app.routing';


export interface NotificheNonLetteDTO {
    idUtente?: string;
    total?: number;
}

interface NotificheDTO {
    code?: number;
    message?: string;
    body: NotificheNonLetteDTO[];
}

@Component({
    selector: 'notifications',
    templateUrl: './notifications.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'notifications',
    standalone: false
})
export class NotificationsComponent extends AbstractDefaultComponent implements OnInit, OnDestroy {
    @ViewChild('notificationsOrigin') private _notificationsOrigin: MatButton;
    @ViewChild('notificationsPanel') private _notificationsPanel: TemplateRef<any>;

    unreadCount: number = undefined;
    private _overlayRef: OverlayRef;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    loadingData = false;
      currentPage: number = -1;
    elementiTotali: number;
    loadingAll: boolean;
    isFirstMessage: boolean = true;
    notifiche= [];


    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _notificationsService: NotificationsService,
        private _overlay: Overlay,
        private _viewContainerRef: ViewContainerRef,
        private localStorageService: LocalStorageService,
        private webSocketService: WebSocketService,
        private router: Router,
        private translocoService: TranslocoService,
        private fuseConfirmationService: FuseConfirmationService,
    ) {
        super();

    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        //this.webSocketService.connect();
        this.subscribeMessage();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        this.webSocketService?.disconnect();
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
        // Dispose the overlay
        if (this._overlayRef) {
            this._overlayRef.dispose();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open the notifications panel
     */
    openPanel(): void {
        this.notifiche = [];
        this.currentPage = -1;
        this.getData();
        // Return if the notifications panel or its origin is not defined
        if (!this._notificationsPanel || !this._notificationsOrigin) {
            return;
        }

        // Create the overlay if it doesn't exist
        if (!this._overlayRef) {
            this._createOverlay();
        }

        // Attach the portal to the overlay
        this._overlayRef.attach(new TemplatePortal(this._notificationsPanel, this._viewContainerRef));
    }

    /**
     * Close the notifications panel
     */
    closePanel(): void {
        this._overlayRef.detach();
    }

    /**
     * Mark all notifications as read
     */
    markAllAsRead(): void {
       // this.loadingAll = true
    }


    /**
     * Toggle read status of the given notification
     */
    toggleRead(notifica): void {
        if(!notifica.isLetto){
            notifica.loading = true
        }
    }


    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------


    /**
     * Create the overlay
     */
    private _createOverlay(): void {
        // Create the overlay
        this._overlayRef = this._overlay.create({
            hasBackdrop: true,
            backdropClass: 'fuse-backdrop-on-mobile',
            scrollStrategy: this._overlay.scrollStrategies.block(),
            positionStrategy: this._overlay.position()
                .flexibleConnectedTo(this._notificationsOrigin._elementRef.nativeElement)
                .withLockedPosition(true)
                .withPush(true)
                .withPositions([
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top'
                    },
                    {
                        originX: 'start',
                        originY: 'top',
                        overlayX: 'start',
                        overlayY: 'bottom'
                    },
                    {
                        originX: 'end',
                        originY: 'bottom',
                        overlayX: 'end',
                        overlayY: 'top'
                    },
                    {
                        originX: 'end',
                        originY: 'top',
                        overlayX: 'end',
                        overlayY: 'bottom'
                    }
                ])
        });

        // Detach the overlay from the portal on backdrop click
        this._overlayRef.backdropClick().subscribe(() => {
            this._overlayRef.detach();
        });
    }


    onTableScroll(e) {
        const tableViewHeight = e.target.offsetHeight
        const tableScrollHeight = e.target.scrollHeight
        const scrollLocation = e.target.scrollTop;
        const buffer = 100;
        const limit = tableScrollHeight - tableViewHeight - buffer;
        if (!this.loadingData && scrollLocation > limit && this.elementiTotali > this.notifiche!.length!) {
            this.getData();
        }
    }

    private getData() {
        this.loadingData = false;

    }

    private subscribeMessage() {
        const id = this.localStorageService.getId();
        this.webSocketService.messageOsb$.subscribe((value) => {
            if (value) {
                const message: NotificheDTO = JSON.parse(value) as NotificheDTO;
                const utenteMessage = message.body.find(b => b.idUtente === id)
                if (!this.isFirstMessage && utenteMessage?.total > this.unreadCount) {
                    this.fuseConfirmationService.openSnackBar({
                        message: getTransalate(this.translocoService, 'common.nuova_notifica'),
                        type: SnackbarTypes.Success
                    })
                }
                if(utenteMessage){
                    this.isFirstMessage = false;
                    this.unreadCount = utenteMessage?.total || undefined;
                    this._changeDetectorRef.detectChanges();
                }

            }
        })
    }

    goTo(notifica) {

    }
}

